/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-01-07 12:18:00                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-14 11:39:10                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * DynamicListSelect: Allow selection in dynamic list
    * useEffect: Transform value to be printed in select 
    * getDynList: get dynamic list
    * handleChange: manage change event
*/

/* Imports */
import AsyncCreatableSelect from "react-select/async-creatable";
import { useTranslation } from "react-i18next";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
import { useEffect, useState } from "react";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
/***/

const localStaticOptionsRepository = {
  "period-types": [
    {value: "day", label: "Day"},
    {value: "week", label: "Week"},
    {value: "month", label: "Month"}
  ],
  "period-types-week-month": [
    {value: "week", label: "Week"},
    {value: "month", label: "Month"}
  ],
  "wo-status-export": [
    {value: "CLOSED", label: "CLOSED"},
    {value: "VALIDATED", label: "VALIDATED"},
    {value: "INVOICED", label: "INVOICED"},
    {value: "PAID", label: "PAID"}
  ]
};

/**
* DynamicListSelect: Allow selection in dynamic list
* @param data - previous selected data
* @param ressource - dynamic list name
* @param onChange - on data change event
* @param className - additional classname
* @param isMulti - allow multiple selection
* @param placeholder - placeholder text for the select input
* @param classNamePrefix - prefix for react-select classNames
*/
export default function DynamicListSelect<T extends {label: string}>({
  data,
  ressource,
  onChange,
  className = "",
  isMulti = false,
  placeholder,
  classNamePrefix = "react-select",
  disabled
}: {
  data?: T | T[],
  ressource: string,
  onChange: (e: T | T[]) => void,
  className?: string,
  isMulti?: boolean,
  placeholder?: string,
  classNamePrefix?: string,
  disabled?: boolean
}) {
  const { t } = useTranslation();
  const [valueState, setValueState] = useState<any>(null);
  const defaultPlaceholder = placeholder || t("common:selectValue");
  const ressources = [ // list of matches between name and url
    {name: "wo-type", path: "/workorder_types"},
    {name: "wo-origin", path: "/workorder_origins"},
    {name: "ma-type", path: "/machine_types"},
    {name: "ma-brand", path: "/machine_brands"},
    {name: "cu-activity", path: "/customer_activity"},
    {name: "cu-languages", path: "/languages"}
  ];

  /**
  * useEffect: Transform value to be printed in select 
  * @param value - reload on value change
  */
  useEffect(() => {
    if (data) {
      if (isMulti) {
        const tmp = data as T[];
        setValueState(tmp.map(el => ({value: el, label: el.label})));
      } else {
        const tmp = data as T;
        if (ressource === "ma-brand" && (tmp as any).type) {
          setValueState({value: tmp, label: `${tmp.label} (${(tmp as any).type.label})`});
        } else {
          setValueState({value: tmp, label: tmp.label});
        }
      }
    }
    else setValueState(null);
  }, [data, isMulti, ressource]);
  /***/

  /**
  * getDynList: get dynamic list 
  * @return - printable list on select
  */
  const getDynList = (inputValue?: string): Promise<EasyTech.IFormData<T>[]> => {
    return new Promise((resolve) => {
      if (ressource && localStaticOptionsRepository.hasOwnProperty(ressource)) {
        const staticOptions = localStaticOptionsRepository[ressource as keyof typeof localStaticOptionsRepository];
        let filteredOptions = staticOptions;
        if (inputValue) {
          filteredOptions = staticOptions.filter(option => 
            option.label.toLowerCase().includes(inputValue.toLowerCase())
          );
        }
        const optionsForSelect = filteredOptions.map(opt => ({
          value: opt as unknown as T, 
          label: opt.label
        }));
        return resolve(optionsForSelect);
      }
      
      const r = ressources.find((el) => el.name === ressource);
      let queries: { queries: Array<{ type: string; operator: string; field: string[]; value: string | undefined; }> } = {
        queries: [{
          type: "SEARCH",
          operator: "LIKE",
          field: ["label", "id"],
          value: inputValue,
        }]
      };

      if (!inputValue) { 
        queries = {queries: []};
      }

      if (r) {
        api.post("users"+r?.path+"/search", queries)
          .then((ret: any) => {
            const dataList = ret.data as T[] || ret as T[];

            if (ressource === "ma-brand") {
              return resolve(dataList.map(el => ({
                value: el,
                label: `${el.label}${(el as any).type ? ` (${(el as any).type.label})` : ""}`
              })));
            }
            return resolve(setSelectList<T>(dataList, (el) => (el.label)));
          })
          .catch(() => resolve([]));
      } else {
        return resolve([]);
      }
    });
  };
  /***/

  /**
  * handleChange: manage change event
  * @param e - new value 
  */
  const handleChange = (e: EasyTech.IFormData<T> | EasyTech.IFormData<T>[]) => {
    if (isMulti) {
      const result = (e as EasyTech.IFormData<T>[]).map((el) => el.value);
      onChange(result);
    } else {
      const result = (e as EasyTech.IFormData<T>).value;
      onChange(result);
    }
  };
  /***/

  /**
  * handleChange: manage change event
  * @param e - new value 
  */
  const createOption = (value: string) => {
    const r = ressources.find((el) => el.name === ressource);
      
    if (r) {
      api.post("admin"+r?.path, {label: value.toUpperCase()})
        .then((ret: any) => {
          handleChange({label: ret.label, value: ret});
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };
  /***/

  const SelectField = isMulti ? MultiCheckSelect : AsyncCreatableSelect;

  return (
    <SelectField   key={isMulti && data ? JSON.stringify((data as T[]).map(item => (item as any).id)) : "default"}
                  cacheOptions
                  defaultOptions
                  loadOptions={(inputValue: string) => getDynList(inputValue)}
                  value={valueState}
                  theme={reactSelectTheme}
                  onChange={(e: EasyTech.IFormData<T> | EasyTech.IFormData<T>[] | null) => handleChange(e as EasyTech.IFormData<T> | EasyTech.IFormData<T>[])}
                  className={`w-full mr-2 ${className}`}
                  name="customer"
                  onCreateOption={createOption}
                  placeholder={defaultPlaceholder}
                  classNamePrefix={classNamePrefix}
                  isMulti={isMulti}
                  closeMenuOnSelect={!isMulti}
                  hideSelectedOptions={!isMulti}
                  {...(isMulti ? { creatable: true } : {})}
                  isClearable={false}
                  isDisabled={disabled} />
  );
}
/***/
